// (POC) 자체 헤드리스 트리 코어 로직 — antd Tree 백엔드 대체(Phase D2) 타당성 검증용.
// UI 독립 순수 함수: (1) 확장 상태 기준 visible 평탄화, (2) antd non-strict 트라이스테이트
// 체크박스 상태 계산, (3) 체크 토글 시 자식 캐스케이드 + 조상 재계산.
// ⚠ react-arborist 가 기본 미지원하는 지점(체크박스·제어형 상태)을 자체 구현으로 검증한다.
import type { OmwTreeNode as OmTreeNode } from '../../api/OmwTree'

/** 평탄화된 visible 노드(가상 스크롤 렌더 단위). */
export interface FlatTreeRow {
  node: OmTreeNode
  key: string
  /** 들여쓰기 깊이(0=루트). */
  depth: number
  parentKey: string | null
  hasChildren: boolean
  expanded: boolean
}

/** 노드별 트라이스테이트 체크 상태. */
export interface CheckState {
  checked: boolean
  indeterminate: boolean
}

/**
 * 확장 상태를 반영해 visible 노드를 깊이우선 평탄화한다.
 * expandedKeys 에 없는 노드의 자식은 접힌 것으로 간주해 제외.
 */
export function flattenVisible(
  treeData: OmTreeNode[],
  expandedKeys: ReadonlySet<string>,
): FlatTreeRow[] {
  const rows: FlatTreeRow[] = []
  const walk = (nodes: OmTreeNode[], depth: number, parentKey: string | null): void => {
    for (const node of nodes) {
      const children = node.children ?? []
      const hasChildren = children.length > 0
      const expanded = expandedKeys.has(node.key)
      rows.push({ node, key: node.key, depth, parentKey, hasChildren, expanded })
      if (hasChildren && expanded) {
        walk(children, depth + 1, node.key)
      }
    }
  }
  walk(treeData, 0, null)
  return rows
}

/** key → 노드/부모/자식 인덱스(빠른 조회용). */
interface TreeIndex {
  byKey: Map<string, OmTreeNode>
  parentOf: Map<string, string | null>
  leavesOf: Map<string, string[]> // 각 노드의 리프 자손 키(자기 자신이 리프면 [self])
}

export function buildTreeIndex(treeData: OmTreeNode[]): TreeIndex {
  const byKey = new Map<string, OmTreeNode>()
  const parentOf = new Map<string, string | null>()
  const leavesOf = new Map<string, string[]>()

  const collectLeaves = (node: OmTreeNode): string[] => {
    const children = node.children ?? []
    if (children.length === 0) {
      leavesOf.set(node.key, [node.key])
      return [node.key]
    }
    const acc: string[] = []
    for (const child of children) acc.push(...collectLeaves(child))
    leavesOf.set(node.key, acc)
    return acc
  }

  const walk = (nodes: OmTreeNode[], parent: string | null): void => {
    for (const node of nodes) {
      byKey.set(node.key, node)
      parentOf.set(node.key, parent)
      walk(node.children ?? [], node.key)
    }
  }
  walk(treeData, null)
  for (const root of treeData) collectLeaves(root)
  return { byKey, parentOf, leavesOf }
}

/**
 * antd non-strict 체크 시맨틱으로 노드별 {checked, indeterminate} 를 계산한다.
 * - 리프: checkedLeaves 에 포함되면 checked.
 * - 부모: 모든 리프 자손이 checked 면 checked, 일부만이면 indeterminate.
 * checkedKeys 는 "체크된 리프" 집합을 진실원본으로 본다(antd 도 내부적으로 리프 기준 파생).
 */
export function computeCheckState(
  treeData: OmTreeNode[],
  checkedLeafKeys: ReadonlySet<string>,
  index?: TreeIndex,
): Map<string, CheckState> {
  const idx = index ?? buildTreeIndex(treeData)
  const result = new Map<string, CheckState>()
  for (const [key, leaves] of idx.leavesOf) {
    const total = leaves.length
    let checkedCount = 0
    for (const leaf of leaves) if (checkedLeafKeys.has(leaf)) checkedCount++
    result.set(key, {
      checked: total > 0 && checkedCount === total,
      indeterminate: checkedCount > 0 && checkedCount < total,
    })
  }
  return result
}

/**
 * 노드 체크 토글 → 새 "체크된 리프" 집합. 자손 리프 전체를 next 상태로 캐스케이드.
 * (조상 상태는 computeCheckState 로 파생되므로 여기선 리프만 갱신.)
 */
export function toggleCheck(
  treeData: OmTreeNode[],
  checkedLeafKeys: ReadonlySet<string>,
  key: string,
  nextChecked: boolean,
  index?: TreeIndex,
): Set<string> {
  const idx = index ?? buildTreeIndex(treeData)
  const next = new Set(checkedLeafKeys)
  const leaves = idx.leavesOf.get(key) ?? [key]
  for (const leaf of leaves) {
    if (nextChecked) next.add(leaf)
    else next.delete(leaf)
  }
  return next
}

/**
 * antd onCheck 계약 재현용: 현재 체크된 리프 집합에서 "완전 체크된 노드 키(checked)"와
 * "부분 체크 노드 키(halfChecked)" 를 파생한다.
 */
export function deriveCheckedKeys(
  treeData: OmTreeNode[],
  checkedLeafKeys: ReadonlySet<string>,
  index?: TreeIndex,
): { checked: string[]; halfChecked: string[] } {
  const state = computeCheckState(treeData, checkedLeafKeys, index)
  const checked: string[] = []
  const halfChecked: string[] = []
  for (const [key, s] of state) {
    if (s.checked) checked.push(key)
    else if (s.indeterminate) halfChecked.push(key)
  }
  return { checked, halfChecked }
}
